import { useEffect, useMemo, useState } from 'react'
import { Button, Empty, Skeleton, Tag } from 'antd'
import { ArrowLeftOutlined, DownloadOutlined } from '@ant-design/icons'
import { useNavigate, useParams } from 'react-router-dom'
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts'
import type { TooltipValueType } from 'recharts'
import { getBacktestCompare } from '@/api'
import { useFeedback } from '@/utils/antdApp'
import type { CompareSeries } from '@/types'
import { filterFormatThousandNumber, filterFormatWanYiNumber , filterDiffDays} from '@/utils/filters'
import { colors } from '@/pages/static'

import './index.scss'

type CompareMetric = {
    key: string
    label: string
    unit?: string
    positive?: boolean
    getValue: (detail: CompareSeries) => string | number
}

type CompareChartPoint = {
    point: number
    [key: string]: string | number
}

type LegendPayloadItem = {
    color?: string
    value?: string
}

const metrics: CompareMetric[] = [
    {
        key: 'range',
        label: '回测区间',
        getValue: (detail) => `${detail.config.startDate ?? '--'}\n→ ${detail.config.endDate ?? '--'}`,
    },
    {
        key: 'return',
        label: '总收益率',
        unit: '%',
        positive: true,
        getValue: (detail) => detail.results.totalReturn ?? '--',
    },
    {
        key: 'annual',
        label: '年化收益率',
        unit: '%',
        positive: true,
        getValue: (detail) => detail.results.annualReturn ?? '--',
    },
    {
        key: 'sharpe',
        label: '夏普比率',
        getValue: (detail) => detail.results.sharpeRatio ?? '--',
    },
    {
        key: 'profitFactor',
        label: '盈亏比率',
        positive: true,
        getValue: (detail) => detail.results.profitFactor ?? '--',
    },
    {
        key: 'drawdown',
        label: '最大回撤',
        unit: '%',
        positive: true,
        getValue: (detail) => detail.results.maxDrawdown ?? '--',
    },
    {
        key: 'winRate',
        label: '胜率',
        unit: '%',
        positive: true,
        getValue: (detail) => detail.results.winRate ?? '--',
    },
    {
        key: 'alpha',
        label: 'Alpha',
        unit: '%',
        positive: true,
        getValue: (detail) => detail.results?.alpha ?? '--',
    },
    {
        key: 'trades',
        label: '交易次数',
        getValue: (detail) => detail.results.totalTrades ?? '--',
    },
    {
        key: 'vsBenchmark',
        label: '超额收益',
        unit: '%',
        positive: true,
        getValue: (detail) => detail.results?.excessReturn ?? '--',
    },
]

function normalizeRunIds(runIdsParam?: string) {
    return (runIdsParam || '')
        .split(',')
        .map((item) => Number(item))
        .filter(Boolean)
}

function formatMetricValue(value: string | number, unit?: string) {
    if (value === '--' || value == null) {
        return '--'
    }

    if (typeof value === 'string') {
        return value
    }

    if (Number.isInteger(value) && !unit) {
        return String(value)
    }

    return `${filterFormatThousandNumber(value)}${unit ?? ''}`
}

function buildChartData(seriesList: CompareSeries[]) {
    const maxLength = Math.max(...seriesList.map((series) => series.results.curveValues?.length ?? 0), 0)

    return Array.from({ length: maxLength }, (_, index) => {
        const point: CompareChartPoint = { point: index + 1 }

        seriesList.forEach((series) => {
            const value = series.results.curveValues?.[index]
            if (typeof value === 'number') {
                point[String(series.id)] = value
            }
        })

        return point
    })
}

function getChartScale(chartData: CompareChartPoint[]) {
    const values = chartData.flatMap((item) =>
        Object.entries(item)
            .filter(([key, value]) => key !== 'point' && typeof value === 'number')
            .map(([, value]) => value as number),
    )

    if (!values.length) {
        return {
            domain: [0, 1] as [number, number],
            ticks: [0, 0.25, 0.5, 0.75, 1],
            decimals: 2,
        }
    }

    const rawMinValue = Math.min(...values)
    const rawMaxValue = Math.max(...values)
    const valueRange = rawMaxValue - rawMinValue
    const valueMagnitude = Math.max(Math.abs(rawMinValue), Math.abs(rawMaxValue), 0.0001)
    const hasSmallVariance =
        valueRange > 0 && (valueRange <= 1 || valueRange / valueMagnitude < 0.05)

    const padding = valueRange === 0
        ? Math.max(valueMagnitude * 0.01, 0.01)
        : hasSmallVariance
            ? Math.max(valueRange * 0.18, 0.02)
            : Math.max(valueRange * 0.12, 0.1)

    const min = rawMinValue - padding
    const max = rawMaxValue + padding
    const tickCount = 5
    const step = tickCount > 1 ? (max - min) / (tickCount - 1) : 0
    const decimals = valueRange < 0.5 ? 3 : valueRange < 5 ? 2 : 1

    const ticks = Array.from({ length: tickCount }, (_, index) =>
        Number((min + step * index).toFixed(decimals + 1)),
    )

    return {
        domain: [min, max] as [number, number],
        ticks,
        decimals,
    }
}

function getToneClass(value: string | number, positive?: boolean) {
    if (!positive || typeof value !== 'number' || value === 0) {
        return ''
    }
    return value > 0 ? 'is-positive' : 'is-negative'
}

function getLegendName(series: CompareSeries) {
    const version = series.version_no != null ? `${series.version_no}` : '--'
    return `${series.manager_nickname} ${version} · ${series.manager_style}`
}

function renderLegend(payload?: LegendPayloadItem[]) {
    if (!payload?.length) {
        return null
    }

    console.log(payload)

    return (
        <div className="compare-backtest__chart-legend">
            {payload.map((entry, index) => (
                <div key={`${entry.value}-${index}`} className="compare-backtest__chart-legend-item">
                    <span
                        className="compare-backtest__chart-legend-line"
                        style={{ backgroundColor: entry.color || '#4c8dff' }}
                    />
                    <span className="compare-backtest__chart-legend-text color-2">{entry.value?.split('·')[0]}</span>
                    <span className="compare-backtest__chart-legend-text">· {entry.value?.split('·')[1]}</span>
                </div>
            ))}
        </div>
    )
}

function CompareSummaryCard({
    series,
    color,
}: {
    series: CompareSeries
    color: string
}) {
    return (
        <article className="compare-backtest__summary-card" style={{ ['--compare-color' as string]: color }}>
            <div className='compare-backtest__summary-card-accent'></div>
            <div className='compare-backtest__summary-card-body'>
                <div className="compare-backtest__summary-head">
                    {series.manager_nickname} 
                </div>
                <div className="compare-backtest__summary-title font-weight-700">{series.name}</div>
                <div className="compare-backtest__summary-time">
                    {series.config.startDate ?? '--'} ~ {series.config.endDate ?? '--'} · {filterDiffDays(series.config.startDate, series.config.endDate)}天
                </div>
                <div className={`compare-backtest__summary-return font-weight-700 ${series.results.totalReturn > 0 ? 'red' : 'green'}`}>
                    {series.results.totalReturn > 0 ? '+' : ''}{filterFormatWanYiNumber(series.results.totalReturn ?? 0)}%
                </div>
            </div>
        </article>
    )
}

export default function CompareBacktestPage() {
    const { message } = useFeedback()
    const navigate = useNavigate()
    const { ids } = useParams<{ ids: string }>()
    const [loading, setLoading] = useState(true)
    const [seriesList, setSeriesList] = useState<CompareSeries[]>([])
    const [managerCount, setManagerCount] = useState<number>(0)

    console.log(ids)

    const normalizedRunIds = useMemo(() => normalizeRunIds(ids), [ids])
    const chartData = useMemo(() => buildChartData(seriesList), [seriesList])
    const chartScale = useMemo(() => getChartScale(chartData), [chartData])
    
    console.log(normalizedRunIds)
    useEffect(() => {
        let cancelled = false

        setLoading(true)

        getBacktestCompare(normalizedRunIds)
            .then((data) => {
                if (cancelled) {
                    return
                }
                console.log(data)
                setSeriesList(data.entries)
                setManagerCount(data.manager_count)
            })
            .catch(() => {
                if (cancelled) {
                    return
                }
                message.error('回测对比数据加载失败')
                setSeriesList([])
                setManagerCount(0)
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })

        return () => {
            cancelled = true
        }
    }, [navigate, normalizedRunIds])

    if (loading) {
        return (
            <div className="compare-backtest">
                <div className="page-header compare-backtest__header">
                    <div className="compare-backtest__header-left">
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/backtests')}>
                            返回列表
                        </Button>
                        <div className="compare-backtest__headline">
                            /
                            <span className="compare-backtest__headline-title font-weight-700">多回测对比</span>
                        </div>
                    </div>
                </div>
                <div className="page-content compare-backtest__content">
                    <div className="compare-backtest__body">
                        <Skeleton active paragraph={{ rows: 12 }} />
                    </div>
                </div>
            </div>
        )
    }

    if (!seriesList.length) {
        return (
            <div className="compare-backtest">
                <div className="page-header compare-backtest__header">
                    <div className="compare-backtest__header-left">
                        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/backtests')}>
                            返回列表
                        </Button>
                        <div className="compare-backtest__headline">
                            /
                            <span className="compare-backtest__headline-title">多回测对比</span>
                        </div>
                    </div>
                </div>
                <div className="page-content compare-backtest__content">
                    <div className="compare-backtest__body">
                        <Empty description="暂无可展示的对比数据" />
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="compare-backtest" >
            <div className="page-header compare-backtest__header">
                <div className="compare-backtest__header-left">
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/backtests')}>
                        返回列表
                    </Button>
                    <div className="compare-backtest__headline">
                        /
                        <span className="compare-backtest__headline-title">多回测对比</span>
                        <Tag className={`compare-backtest__headline-tag ${managerCount === 1 ? 'is-purple' : 'is-ginger'}`}>
                            {managerCount === 1 ? `同经理版本对比 · ${seriesList[0].manager_nickname} `: `跨经理策略对比 · ${managerCount}位经理`}
                        </Tag>
                    </div>
                </div>
                <div className="compare-backtest__header-right">
                    <Button icon={<DownloadOutlined />}>
                        导出PDF
                    </Button>
                </div>
            </div>

            <div className="page-content compare-backtest__content">
                <div className="compare-backtest__body">
                    <section className="compare-backtest__summary-grid">
                        {seriesList.map((series, index) => (
                            <CompareSummaryCard
                                key={series.id}
                                series={series}
                                color={colors[index % colors.length]}
                            />
                        ))}
                    </section>

                    <section className="compare-backtest__panel">
                        <div className="compare-backtest__section-title font-weight-700">核心指标对比</div>
                        <div className="compare-backtest__table-wrap">
                            <table className="compare-backtest__table">
                                <thead>
                                    <tr>
                                        <th>指标</th>
                                        {seriesList.map((series, index) => (
                                            <th key={series.id}>
                                                <div className="compare-backtest__table-head">
                                                    <span
                                                        className="compare-backtest__legend-dot"
                                                        style={{ backgroundColor: colors[index % colors.length] }}
                                                    />
                                                    <span className="compare-backtest__table-name">{series.name}</span>
                                                </div>
                                                <div className="compare-backtest__table-sub">
                                                    {series.manager_style || '策略'} · {series.version_no?? '--'}
                                                </div>
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {metrics.map((metric) => (
                                        <tr key={metric.key}>
                                            <td>{metric.label}</td>
                                            {seriesList.map((series) => {
                                                const value = metric.getValue(series)
                                                const isMultiline = typeof value === 'string' && value.includes('\n')
                                                return (
                                                    <td key={`${metric.key}-${series.id}`} className={getToneClass(value, metric.positive)}>
                                                        {isMultiline ? (
                                                            <span className="compare-backtest__multiline">
                                                                {String(value).split('\n').map((line) => (
                                                                    <span key={line}>{line}</span>
                                                                ))}
                                                            </span>
                                                        ) : (
                                                            formatMetricValue(value, metric.unit)
                                                        )}
                                                    </td>
                                                )
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <div className="compare-backtest__table-footer font-weight-700">
                                <span className="green">最优</span>
                                <span className="red">最差</span>
                                <span>· 仅在各列有差异时高亮</span>
                            </div>
                        </div>
                    </section>

                    <section className="compare-backtest__panel">
                        <div className="compare-backtest__section-title">收益率走势对比</div>
                        <div className="compare-backtest__warning">⚠ 各回测时间段不同，图表已归一至起点100基点，横向比较相对收益表现</div>

                        <div className="compare-backtest__chart-card">
                            <div className="compare-backtest__chart">
                                <ResponsiveContainer width="100%" height={360}>
                                    <LineChart data={chartData} margin={{ top: 16, right: 12, left: 8, bottom: 12 }}>
                                        <CartesianGrid vertical={false} stroke="rgba(148, 163, 184, 0.08)" />
                                        <YAxis
                                            domain={chartScale.domain}
                                            ticks={chartScale.ticks}
                                            axisLine={false}
                                            tickLine={false}
                                            tick={{ fill: '#64748b', fontSize: 11 }}
                                            tickFormatter={(value: number) => value.toFixed(chartScale.decimals)}
                                            width={56}
                                        />
                                        <Tooltip
                                            labelFormatter={(label) => `点位 ${label}`}
                                            formatter={(value: TooltipValueType | undefined, name: string | number | undefined) => [`${Number(value ?? 0).toFixed(2)}`, name ?? '']}
                                            contentStyle={{
                                                background: '#111a2e',
                                                borderRadius: 12,
                                                border: '1px solid rgba(148, 163, 184, 0.12)',
                                                color: '#e2e8f0',
                                                fontSize: 11,
                                            }}
                                        />
                                        <Legend
                                            verticalAlign="bottom"
                                            align="left"
                                            wrapperStyle={{ left: 0, bottom: 0 }}
                                            content={({ payload }) => renderLegend(payload as LegendPayloadItem[])}
                                        />
                                        {seriesList.map((series, index) => (
                                            <Line
                                                key={series.id}
                                                name={getLegendName(series)}
                                                type="monotone"
                                                dataKey={String(series.id)}
                                                stroke={colors[index % colors.length]}
                                                strokeWidth={2.5}
                                                dot={false}
                                                activeDot={{ r: 4 }}
                                                connectNulls
                                                isAnimationActive={false}
                                            />
                                        ))}
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>

                            <div className="compare-backtest__range-list">
                                {seriesList.map((series, index) => (
                                    <div key={series.id} className="compare-backtest__range-pill" style={{ ['--compare-color' as string]: colors[index % colors.length] }}>
                                        <span
                                            className="compare-backtest__legend-dot"
                                            style={{ backgroundColor: colors[index % colors.length] }}
                                        />
                                        {series.config.startDate} ~ {series.config.endDate}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="compare-backtest__desc-grid">
                        <div className="compare-backtest__section-title">策略描述</div>
                        <article className="compare-backtest__desc-card-body">
                            {seriesList.map((series, index) => (
                                <article
                                    key={series.id}
                                    className="compare-backtest__desc-card"
                                    style={{ ['--compare-color' as string]: colors[index % colors.length] }}
                                >
                                    <div className='compare-backtest__summary-card-accent'></div>
                                    <div className='compare-backtest__summary-card-body'>
                                        <div className="compare-backtest__desc-title font-weight-700">
                                            {series.manager_nickname}
                                        </div>
                                        <div className="compare-backtest__desc-text">
                                            {series.strategy_snapshot?.trim() || '暂无策略描述'}
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </article>
                    </section>
                </div>
            </div>
        </div>
    )
}
